/**
 * Surface команды `task_edit`.
 * Загружает задачу из payload и дает встроенную форму редактирования
 * внутри `ActivityContextView`.
 */
import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Group, Loader, Stack, TextInput, Textarea } from '@mantine/core';
import logger from '@ayocore/platform/common/helpers/logger';
import { useTaskRepository } from '@ayocore/tasks/client/rpc';
import type { Task } from '@ayocore/tasks/schemas/task.schema';
import { extractActivityTaskId } from '@ayocore/activity/common/types/activity-command-context';
import type { ActivitySlashCommandFormProps } from '@ayocore/activity/client/extensions';

function formatDateInputValue(raw: Date | string | null | undefined): string {
  if (!raw) return '';
  const date = raw instanceof Date ? raw : new Date(raw);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Surface для редактирования задачи в едином host (`ActivityContextView`).
 */
export function ActivityTaskEditCommandForm({
  context,
  onCancel,
  onSubmitted,
}: ActivitySlashCommandFormProps) {
  const taskRepository = useTaskRepository();
  const taskId = useMemo(() => extractActivityTaskId(context.payload), [context.payload]);

  const [task, setTask] = useState<Task | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [dueTime, setDueTime] = useState('');

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!taskId) {
      setTask(null);
      setErrorMessage('Не удалось определить задачу для редактирования.');
      return;
    }

    let disposed = false;
    setLoading(true);
    setErrorMessage(null);
    void (async () => {
      try {
        const loaded = await taskRepository.findById(taskId);
        if (disposed) return;
        if (!loaded) {
          setTask(null);
          setErrorMessage('Задача не найдена.');
          return;
        }
        setTask(loaded);
        setTitle(typeof loaded.title === 'string' ? loaded.title : '');
        setDescription(typeof loaded.description === 'string' ? loaded.description : '');
        setDueDate(formatDateInputValue(loaded.due_date ?? null));
        setDueTime(typeof loaded.due_time === 'string' ? loaded.due_time.slice(0, 5) : '');
      } catch (error) {
        if (disposed) return;
        logger.error('[ActivityTaskEditCommandForm] Failed to load task', { taskId, error });
        setErrorMessage('Не удалось загрузить задачу.');
      } finally {
        if (!disposed) {
          setLoading(false);
        }
      }
    })();

    return () => {
      disposed = true;
    };
  }, [taskId, taskRepository]);

  const canSubmit = title.trim().length > 0 && !submitting && !loading;

  const handleSubmit = async (): Promise<void> => {
    if (!taskId || !canSubmit) return;
    setSubmitting(true);
    setErrorMessage(null);
    try {
      const normalizedDueDate = dueDate.trim() ? (() => {
        const [y, m, d] = dueDate.trim().split('-').map(Number);
        if (!y || !m || !d) return null;
        // UTC-полдень: исключает day-drift при любой TZ сервера/клиента
        return new Date(Date.UTC(y, m - 1, d, 12, 0, 0, 0));
      })() : null;
      const dueDateValue =
        normalizedDueDate && !Number.isNaN(normalizedDueDate.getTime()) ? normalizedDueDate : null;

      await taskRepository.update(taskId, {
        title: title.trim(),
        description: description.trim() || null,
        due_date: dueDateValue,
        due_time: dueTime.trim() ? `${dueTime.trim()}:00` : null,
      });
      onSubmitted();
    } catch (error) {
      logger.error('[ActivityTaskEditCommandForm] Failed to update task', { taskId, error });
      setErrorMessage('Не удалось обновить задачу.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Stack gap='xs'>
      {loading ? (
        <Group gap={6}>
          <Loader size={12} />
          <span>Загрузка задачи...</span>
        </Group>
      ) : null}

      {errorMessage ? (
        <Alert color='red' p='xs' title='Ошибка'>
          {errorMessage}
        </Alert>
      ) : null}

      {!loading && task ? (
        <>
          <TextInput
            label='Название'
            size='xs'
            value={title}
            onChange={event => setTitle(event.currentTarget.value)}
            placeholder='Название задачи'
          />
          <Textarea
            label='Описание'
            size='xs'
            minRows={2}
            autosize
            value={description}
            onChange={event => setDescription(event.currentTarget.value)}
            placeholder='Описание задачи'
          />
          <Group grow>
            <TextInput
              type='date'
              label='Дата'
              size='xs'
              value={dueDate}
              onChange={event => setDueDate(event.currentTarget.value)}
            />
            <TextInput
              type='time'
              label='Время'
              size='xs'
              value={dueTime}
              onChange={event => setDueTime(event.currentTarget.value)}
            />
          </Group>
        </>
      ) : null}

      <Group justify='flex-end'>
        <Button variant='light' color='gray' size='xs' onClick={onCancel}>
          Отмена
        </Button>
        <Button
          size='xs'
          onClick={() => {
            void handleSubmit();
          }}
          loading={submitting}
          disabled={!canSubmit}
        >
          Сохранить
        </Button>
      </Group>
    </Stack>
  );
}
